import {
  isValidReplayCoverage,
  isValidReplayDatasetRecord,
  isValidReplaySessionMetadata,
  REPLAY_DATE_CACHE_CAPACITY,
  replayDatasetRecordKey,
} from '../Utils/replayV2';

const DB_NAME = 'spx-playback-history';
const DB_VERSION = 3;
export const PLAYBACK_HISTORY_SCHEMA_VERSION = 2;
export { REPLAY_DATE_CACHE_CAPACITY };
const SNAPSHOT_STORE = 'rowSnapshots';
const META_STORE = 'metadata';
const SOURCE_RECORD_STORE = 'sourceRecords';
const SOURCE_SESSION_STORE = 'sourceSessions';
const ACTIVE_DAY_KEY = 'activeDay';
const REPLAY_STATE_KEY = 'replayState';
const REPLAY_DATE_ACCESS_KEY = 'replayDateAccess';
const REPLAY_STATE_DEBOUNCE_MS = 250;

let historyDriver = createIndexedDbDriver();
let dbPromise = null;
let replayStateWrite = Promise.resolve();
let replayStateTimer = null;
let pendingReplayState = null;
let pendingReplayStateWaiters = [];

function snapshotKey(day, minute) {
  return `${day}:${minute}`;
}

function sourceRecordKey(day, dataset, record) {
  return `${day}:${dataset}:${replayDatasetRecordKey(dataset, record)}`;
}

function replayDayFromKey(key) {
  return typeof key === 'string' ? key.split(':', 1)[0] : null;
}

function compareReplayDaysByAccess(access = {}) {
  return (left, right) => (
    Number(access[left] || 0) - Number(access[right] || 0)
    || left.localeCompare(right)
  );
}

function createIndexedDbDriver() {
  return {
    async loadDay(day) {
      const db = await openDb();
      const range = IDBKeyRange.bound(`${day}:`, `${day};`, false, true);
      return withStore(db, SNAPSHOT_STORE, 'readonly', (store) => store.getAll(range));
    },
    async saveSnapshot(snapshot, { resetDay = false, activateDay = true } = {}) {
      const db = await openDb();
      return withTransaction(db, [SNAPSHOT_STORE, META_STORE], 'readwrite', (tx, setResult) => {
        const snapshots = tx.objectStore(SNAPSHOT_STORE);
        if (resetDay) snapshots.clear();
        snapshots.put(snapshot, snapshotKey(snapshot.day, snapshot.minute));
        const metadata = tx.objectStore(META_STORE);
        if (activateDay) metadata.put(snapshot.day, ACTIVE_DAY_KEY);
        const accessRequest = metadata.get(REPLAY_DATE_ACCESS_KEY);
        accessRequest.onsuccess = () => {
          const access = accessRequest.result || {};
          const isNew = !Object.prototype.hasOwnProperty.call(access, snapshot.day);
          metadata.put({ ...access, [snapshot.day]: Date.now() }, REPLAY_DATE_ACCESS_KEY);
          setResult(isNew);
        };
      });
    },
    async saveSnapshots(
      snapshotBatch,
      { resetDay = false, activateDay = true, signal = null } = {},
    ) {
      if (!snapshotBatch.length) return;
      const db = await openDb();
      return withTransaction(db, [SNAPSHOT_STORE, META_STORE], 'readwrite', (tx, setResult) => {
        const snapshots = tx.objectStore(SNAPSHOT_STORE);
        if (resetDay) snapshots.clear();
        snapshotBatch.forEach((snapshot) => {
          snapshots.put(snapshot, snapshotKey(snapshot.day, snapshot.minute));
        });
        const day = snapshotBatch[0].day;
        const metadata = tx.objectStore(META_STORE);
        if (activateDay) metadata.put(day, ACTIVE_DAY_KEY);
        const accessRequest = metadata.get(REPLAY_DATE_ACCESS_KEY);
        accessRequest.onsuccess = () => {
          const access = accessRequest.result || {};
          const isNew = !Object.prototype.hasOwnProperty.call(access, day);
          metadata.put({ ...access, [day]: Date.now() }, REPLAY_DATE_ACCESS_KEY);
          setResult(isNew);
        };
      }, signal);
    },
    async loadReplayState() {
      const db = await openDb();
      return withStore(db, META_STORE, 'readonly', (store) => store.get(REPLAY_STATE_KEY));
    },
    async saveReplayState(value) {
      const db = await openDb();
      return withStore(db, META_STORE, 'readwrite', (store) => store.put(value, REPLAY_STATE_KEY));
    },
    async loadActiveDay() {
      const db = await openDb();
      return withStore(db, META_STORE, 'readonly', (store) => store.get(ACTIVE_DAY_KEY));
    },
    async loadReplayDatasetSession(day, { includeRecords = true } = {}) {
      const db = await openDb();
      const session = await withStore(db, SOURCE_SESSION_STORE, 'readonly', (store) => store.get(day));
      if (!session || !includeRecords) return session || null;
      const range = IDBKeyRange.bound(`${day}:`, `${day};`, false, true);
      const rows = await withStore(db, SOURCE_RECORD_STORE, 'readonly', (store) => store.getAll(range));
      const datasets = {};
      for (const row of rows || []) {
        if (!datasets[row.dataset]) datasets[row.dataset] = [];
        datasets[row.dataset].push(row.record);
      }
      return { ...session, datasets };
    },
    async touchReplayDay(day) {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(META_STORE, 'readwrite');
        const metadata = tx.objectStore(META_STORE);
        let isNew = false;
        const request = metadata.get(REPLAY_DATE_ACCESS_KEY);
        request.onsuccess = () => {
          const access = request.result || {};
          isNew = !Object.prototype.hasOwnProperty.call(access, day);
          metadata.put({ ...access, [day]: Date.now() }, REPLAY_DATE_ACCESS_KEY);
        };
        request.onerror = () => reject(request.error || new Error('IndexedDB request failed.'));
        tx.oncomplete = () => resolve(isNew);
        tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted.'));
        tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed.'));
      });
    },
    async pruneReplayDays(capacity = REPLAY_DATE_CACHE_CAPACITY) {
      const db = await openDb();
      return withTransaction(
        db,
        [SNAPSHOT_STORE, SOURCE_RECORD_STORE, SOURCE_SESSION_STORE, META_STORE],
        'readwrite',
        (tx, setResult) => {
          const metadata = tx.objectStore(META_STORE);
          const accessRequest = metadata.get(REPLAY_DATE_ACCESS_KEY);
          accessRequest.onsuccess = () => {
            const access = accessRequest.result || {};
            const days = Object.keys(access)
              .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day));
            const evictedDays = days
              .sort(compareReplayDaysByAccess(access))
              .slice(0, Math.max(0, days.length - capacity));
            if (!evictedDays.length) {
              setResult([]);
              return;
            }
            const snapshots = tx.objectStore(SNAPSHOT_STORE);
            const records = tx.objectStore(SOURCE_RECORD_STORE);
            const sessions = tx.objectStore(SOURCE_SESSION_STORE);
            for (const day of evictedDays) {
              const range = IDBKeyRange.bound(`${day}:`, `${day};`, false, true);
              snapshots.delete(range);
              records.delete(range);
              sessions.delete(day);
            }
            const nextAccess = { ...access };
            evictedDays.forEach((day) => delete nextAccess[day]);
            metadata.put(nextAccess, REPLAY_DATE_ACCESS_KEY);
            setResult(evictedDays);
          };
        },
      );
    },
    async saveReplayDatasetSession(session, { signal = null } = {}) {
      const db = await openDb();
      const day = session.tradingDate;
      const range = IDBKeyRange.bound(`${day}:`, `${day};`, false, true);
      return withTransaction(
        db,
        [SOURCE_RECORD_STORE, SOURCE_SESSION_STORE, META_STORE],
        'readwrite',
        (tx, setResult) => {
          const records = tx.objectStore(SOURCE_RECORD_STORE);
          records.delete(range);
          for (const [dataset, values] of Object.entries(session.datasets || {})) {
            for (const record of values) {
              records.put({ day, dataset, record }, sourceRecordKey(day, dataset, record));
            }
          }
          const { datasets, ...metadata } = session;
          tx.objectStore(SOURCE_SESSION_STORE).put(metadata, day);
          const metadataStore = tx.objectStore(META_STORE);
          const accessRequest = metadataStore.get(REPLAY_DATE_ACCESS_KEY);
          accessRequest.onsuccess = () => {
            const access = accessRequest.result || {};
            const isNew = !Object.prototype.hasOwnProperty.call(access, day);
            metadataStore.put({ ...access, [day]: Date.now() }, REPLAY_DATE_ACCESS_KEY);
            setResult(isNew);
          };
        },
        signal,
      );
    },
  };
}

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) db.createObjectStore(SNAPSHOT_STORE);
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
      if (!db.objectStoreNames.contains(SOURCE_RECORD_STORE)) db.createObjectStore(SOURCE_RECORD_STORE);
      if (!db.objectStoreNames.contains(SOURCE_SESSION_STORE)) db.createObjectStore(SOURCE_SESSION_STORE);
      if (req.oldVersion < 3) {
        const tx = req.transaction;
        const days = new Set();
        let pending = 3;
        const collect = (storeName, mapper) => {
          const keys = tx.objectStore(storeName).getAllKeys();
          keys.onsuccess = () => {
            (keys.result || []).map(mapper).forEach((day) => {
              if (/^\d{4}-\d{2}-\d{2}$/.test(day || '')) days.add(day);
            });
            pending -= 1;
            if (pending === 0) {
              tx.objectStore(META_STORE).put(
                Object.fromEntries([...days].map((day) => [day, Date.now()])),
                REPLAY_DATE_ACCESS_KEY,
              );
            }
          };
        };
        collect(SNAPSHOT_STORE, replayDayFromKey);
        collect(SOURCE_RECORD_STORE, replayDayFromKey);
        collect(SOURCE_SESSION_STORE, (key) => key);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error || new Error('IndexedDB open failed.'));
    };
  });
  return dbPromise;
}

function withStore(db, storeName, mode, run) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const req = run(tx.objectStore(storeName));
    let result;
    req.onsuccess = () => {
      result = req.result;
      if (mode === 'readonly') resolve(result);
    };
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed.'));
    tx.oncomplete = () => {
      if (mode !== 'readonly') resolve(result);
    };
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted.'));
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed.'));
  });
}

function withTransaction(db, storeNames, mode, run, signal = null) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Replay persistence cancelled.'));
      return;
    }
    const tx = db.transaction(storeNames, mode);
    let result;
    let settled = false;
    const removeAbortListener = () => signal?.removeEventListener('abort', abortTransaction);
    const abortTransaction = () => {
      if (settled) return;
      try {
        tx.abort();
      } catch {
        // The transaction completed between the signal and this callback.
      }
    };
    signal?.addEventListener('abort', abortTransaction, { once: true });
    run(tx, (value) => { result = value; });
    tx.oncomplete = () => {
      settled = true;
      removeAbortListener();
      resolve(result);
    };
    tx.onabort = () => {
      settled = true;
      removeAbortListener();
      reject(tx.error || new Error('IndexedDB transaction aborted.'));
    };
    tx.onerror = () => {
      settled = true;
      removeAbortListener();
      reject(tx.error || new Error('IndexedDB transaction failed.'));
    };
  });
}

export async function loadReplayHistory(fallbackDay, selectedDay = null) {
  const activeDay = await historyDriver.loadActiveDay();
  const day = selectedDay || activeDay || fallbackDay;
  const loadedSnapshots = day ? await historyDriver.loadDay(day) : [];
  const snapshots = (loadedSnapshots || []).filter(
    (snapshot) => snapshot.schemaVersion === PLAYBACK_HISTORY_SCHEMA_VERSION && snapshot.day === day,
  ).sort((a, b) => a.minute - b.minute);
  const replayState = await loadPersistedReplayState();
  if (day && typeof historyDriver.touchReplayDay === 'function') {
    const isNew = await historyDriver.touchReplayDay(day);
    await retainReplayDays(isNew);
  }
  return { day, snapshots, replayState };
}

function sameReplayValue(left, right) {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length || leftKeys.some((key, index) => key !== rightKeys[index])) {
    return false;
  }
  return leftKeys.every((key) => sameReplayValue(left[key], right[key]));
}

export function replayDatasetSessionIsStructurallyValid(session, tradingDate = null) {
  if (
    session?.schemaVersion !== 2
    || !/^\d{4}-\d{2}-\d{2}$/.test(session?.tradingDate || '')
    || (tradingDate && session.tradingDate !== tradingDate)
    || !Number.isFinite(session.generatedAt)
    || !isValidReplaySessionMetadata(session.session)
    || !isValidReplayCoverage(session.coverage)
    || !session.datasets
    || Array.isArray(session.datasets)
    || !session.datasetRecordCounts
    || Array.isArray(session.datasetRecordCounts)
  ) return false;
  for (const [dataset, records] of Object.entries(session.datasets)) {
    if (!session.coverage[dataset] || !Array.isArray(records)
      || session.datasetRecordCounts[dataset] !== records.length
      || !records.every((record) => isValidReplayDatasetRecord(dataset, record))) return false;
  }
  return Object.entries(session.coverage).every(([dataset, entry]) => (
    !['complete', 'partial'].includes(entry?.status)
    || (Array.isArray(session.datasets[dataset])
      && session.datasetRecordCounts[dataset] === session.datasets[dataset].length)
  ));
}

export function replayDatasetSessionMetadataMatchesDiscovery(session, discovery) {
  return Boolean(
    session?.tradingDate
    && discovery?.tradingDate
    && Number.isFinite(session.generatedAt)
    && session.generatedAt >= discovery.generatedAt
    && session.tradingDate === discovery.tradingDate
    && sameReplayValue(session.session, discovery.session)
    && Object.entries(discovery.coverage || {}).every(([dataset, expected]) => {
      const actual = session.coverage?.[dataset];
      return actual?.status === expected?.status
        && actual.observedKeyCount === expected?.observedKeyCount
        && sameReplayValue(actual.availableRanges || [], expected?.availableRanges || [])
        && sameReplayValue(actual.missingRanges || [], expected?.missingRanges || []);
    }),
  );
}

export function replayDatasetSessionMatchesCoverage(session, discovery) {
  if (
    !replayDatasetSessionIsStructurallyValid(session, discovery?.tradingDate)
    || !discovery?.tradingDate
    || !discovery?.coverage
    || !Number.isFinite(session.generatedAt)
    || session.generatedAt < discovery.generatedAt
    || !sameReplayValue(session.session, discovery.session)
    || session.tradingDate !== discovery.tradingDate
  ) return false;
  for (const [dataset, expected] of Object.entries(discovery.coverage)) {
    const actual = session.coverage[dataset];
    if (!actual || actual.status !== expected?.status) return false;
    if (actual.observedKeyCount !== expected?.observedKeyCount) return false;
    if (JSON.stringify(actual.availableRanges || []) !== JSON.stringify(expected?.availableRanges || [])) {
      return false;
    }
    if (JSON.stringify(actual.missingRanges || []) !== JSON.stringify(expected?.missingRanges || [])) {
      return false;
    }
    if (['complete', 'partial'].includes(actual.status) && !Array.isArray(session.datasets[dataset])) {
      return false;
    }
    if (Array.isArray(session.datasets[dataset])
      && session.datasetRecordCounts[dataset] !== session.datasets[dataset].length) return false;
  }
  for (const [dataset, records] of Object.entries(session.datasets)) {
    if (!session.coverage[dataset] || !Array.isArray(records)
      || session.datasetRecordCounts[dataset] !== records.length) return false;
  }
  return true;
}

export function replayDatasetSessionHasCompatibleProvenance(session, discovery) {
  if (
    !replayDatasetSessionIsStructurallyValid(session, discovery?.tradingDate)
    || !replayDatasetSessionMetadataHasCompatibleProvenance(session, discovery)
  ) return false;
  for (const [dataset, records] of Object.entries(session.datasets)) {
    if (!session.coverage[dataset] || !Array.isArray(records)
      || session.datasetRecordCounts[dataset] !== records.length) return false;
  }
  return true;
}

export function replayDatasetSessionMetadataHasCompatibleProvenance(session, discovery) {
  return Boolean(
    session?.tradingDate
    && session?.coverage
    && discovery?.tradingDate
    && Number.isFinite(session.generatedAt)
    && session.generatedAt >= discovery.generatedAt
    && sameReplayValue(session.session, discovery.session)
    && session.tradingDate === discovery.tradingDate,
  );
}

function subtractReplayRange(range, covered = []) {
  let remaining = [{ from: range.from, to: range.to }];
  for (const current of covered) {
    if (!Number.isFinite(current?.from) || !Number.isFinite(current?.to)) continue;
    remaining = remaining.flatMap((candidate) => {
      if (current.to <= candidate.from || current.from >= candidate.to) return [candidate];
      const parts = [];
      if (current.from > candidate.from) parts.push({ from: candidate.from, to: current.from });
      if (current.to < candidate.to) parts.push({ from: current.to, to: candidate.to });
      return parts;
    });
  }
  return remaining;
}

export function replayCoverageMissingRange(session, discovery) {
  const missing = [];
  for (const [dataset, expected] of Object.entries(discovery?.coverage || {})) {
    const actual = session?.coverage?.[dataset];
    for (const range of expected?.availableRanges || []) {
      if (!Number.isFinite(range?.from) || !Number.isFinite(range?.to)) continue;
      missing.push(...subtractReplayRange(range, actual?.availableRanges || []));
    }
  }
  if (!missing.length) return null;
  return {
    from: Math.min(...missing.map((range) => range.from)),
    to: Math.max(...missing.map((range) => range.to)),
  };
}

async function retainReplayDays(isNew) {
  if (isNew && typeof historyDriver.pruneReplayDays === 'function') {
    await historyDriver.pruneReplayDays(REPLAY_DATE_CACHE_CAPACITY);
  }
}

async function touchReplayDay(day) {
  if (typeof historyDriver.touchReplayDay === 'function') {
    return historyDriver.touchReplayDay(day);
  }
  return false;
}

export async function touchReplayHistoryDate(day) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return false;
  const isNew = await touchReplayDay(day);
  await retainReplayDays(isNew);
  return isNew;
}

export async function loadPersistedReplayState() {
  const replayState = await historyDriver.loadReplayState();
  return replayState?.schemaVersion === PLAYBACK_HISTORY_SCHEMA_VERSION
    ? replayState
    : null;
}

function replaySnapshot(day, frame) {
  const minute = Math.floor(frame.t / 60_000);
  return {
    schemaVersion: PLAYBACK_HISTORY_SCHEMA_VERSION,
    day,
    minute,
    t: frame.t,
    rowsBySym: frame.options.rowsBySym,
    spot: frame.options.spot,
    dynamicLevels: frame.greeks.dynamicLevels,
    topOptions: frame.greeks.topOptions,
    aggregationState: frame.aggregationState,
    ...(frame.datasetAnchor === true ? { datasetAnchor: true } : {}),
    ...(frame.partial === true ? { partial: true } : {}),
  };
}

export async function persistReplaySnapshot(day, frame) {
  const snapshot = replaySnapshot(day, frame);
  const transactionTouch = await historyDriver.saveSnapshot(snapshot, { resetDay: false });
  const isNew = typeof transactionTouch === 'boolean'
    ? transactionTouch
    : await touchReplayDay(day);
  await retainReplayDays(isNew);
  return snapshot;
}

export async function persistReplaySnapshots(
  day,
  frames = [],
  { activateDay = true, signal = null } = {},
) {
  const snapshots = frames.map((frame) => replaySnapshot(day, frame));
  if (!snapshots.length) return snapshots;
  if (signal?.aborted) throw new Error('Replay persistence cancelled.');
  const resetDay = false;
  let transactionTouch;
  if (typeof historyDriver.saveSnapshots === 'function') {
    transactionTouch = await historyDriver.saveSnapshots(snapshots, {
      resetDay,
      activateDay,
      ...(signal ? { signal } : {}),
    });
  } else {
    for (let index = 0; index < snapshots.length; index += 1) {
      if (signal?.aborted) throw new Error('Replay persistence cancelled.');
      const savedTouch = await historyDriver.saveSnapshot(snapshots[index], {
        resetDay: resetDay && index === 0,
        activateDay,
        signal,
      });
      if (typeof savedTouch === 'boolean') {
        transactionTouch = Boolean(transactionTouch || savedTouch);
      }
    }
  }
  if (signal?.aborted) throw new Error('Replay persistence cancelled.');
  const isNew = typeof transactionTouch === 'boolean'
    ? transactionTouch
    : await touchReplayDay(day);
  await retainReplayDays(isNew);
  return snapshots;
}

export async function loadReplayDatasetSession(day, options = undefined) {
  if (!day || typeof historyDriver.loadReplayDatasetSession !== 'function') return null;
  return historyDriver.loadReplayDatasetSession(day, options);
}

export async function persistReplayDatasetSession(session, { signal = null } = {}) {
  if (!session?.tradingDate || typeof historyDriver.saveReplayDatasetSession !== 'function') {
    throw new Error('Replay dataset persistence unavailable.');
  }
  if (signal?.aborted) throw new Error('Replay persistence cancelled.');
  const transactionTouch = signal
    ? await historyDriver.saveReplayDatasetSession(session, { signal })
    : await historyDriver.saveReplayDatasetSession(session);
  if (signal?.aborted) throw new Error('Replay persistence cancelled.');
  const isNew = typeof transactionTouch === 'boolean'
    ? transactionTouch
    : await touchReplayDay(session.tradingDate);
  await retainReplayDays(isNew);
  return session;
}

function writeReplayState(value) {
  replayStateWrite = replayStateWrite
    .catch(() => undefined)
    .then(() => historyDriver.saveReplayState({
      schemaVersion: PLAYBACK_HISTORY_SCHEMA_VERSION,
      ...value,
    }));
  return replayStateWrite;
}

function flushPendingReplayState() {
  replayStateTimer = null;
  const value = pendingReplayState;
  const waiters = pendingReplayStateWaiters;
  pendingReplayState = null;
  pendingReplayStateWaiters = [];
  writeReplayState(value).then(
    (result) => waiters.forEach(({ resolve }) => resolve(result)),
    (error) => waiters.forEach(({ reject }) => reject(error)),
  );
}

export function persistReplayState(value, { immediate = false } = {}) {
  pendingReplayState = value;
  const result = new Promise((resolve, reject) => {
    pendingReplayStateWaiters.push({ resolve, reject });
  });
  if (replayStateTimer) clearTimeout(replayStateTimer);
  if (immediate) flushPendingReplayState();
  else replayStateTimer = setTimeout(flushPendingReplayState, REPLAY_STATE_DEBOUNCE_MS);
  return result;
}

export function __setPlaybackHistoryDriver(driver = null) {
  historyDriver = driver || createIndexedDbDriver();
  replayStateWrite = Promise.resolve();
  if (replayStateTimer) clearTimeout(replayStateTimer);
  replayStateTimer = null;
  pendingReplayState = null;
  pendingReplayStateWaiters = [];
  if (dbPromise) dbPromise.then((db) => db.close()).catch(() => undefined);
  dbPromise = null;
}
